<template>
  <div class="upload-field-wrapper shadow">
    <div
      v-bind:class="[ 'upload-field', fileUploadReset ? 'active' : '']"
      @drop.prevent="dropFile"
      @dragover.prevent
    >
      <!-- no file uploaded yet: use css instead og v-if to keep the input accessible (resettable)-->
      <!--OLD: v-if="fileUploadReset"-->
      <div v-bind:class="{'hidden': !fileUploadReset}">
        <!-- upload icons -->
        <img src="@/assets/img/upload.svg">

        <p>
          Drag and drop your
          <span>data.json</span> or
          <span>myData.zip</span> file here
        </p>
        <p style="opacity: 0.5;">OR</p>
        <input type="file" id="uploader" accept=".json, .zip" v-on:change="loadFile">
        <label for="uploader" class="purple-button">Select a file</label>
        <!--<p id="hint">We protect your privacy and anonymize the data in your browser! The file is NOT uploaded to a server!</p>-->
      </div>
      <!--file uploaded-->
      <div v-if="!fileUploadReset && !fileSubmitted">
        <LoadingAnimation v-if="fileUploadingInProgress"/>
        <p v-if="fileUploadingInProgress">Uploading file</p>

        <img v-if="fileUploaded" src="@/assets/img/file-uploaded.svg">
        <div v-if="fileUploaded" class="status-text-wrapper">
          <div>{{file.name}} uploaded</div>
          <img
            class="close-icon pseudo-button"
            src="@/assets/img/close-solid.svg"
            @click="removeFile()"
          >
        </div>
      </div>
      <div v-if="fileSubmitted">
        <LoadingAnimation/>
        <p>Generating visualizations...</p>
      </div>
    </div>

    <!-- checkbox and button outsise the d&d field-->
    <Checkbox v-on:openPopup="openPopup" v-on:checkTC="checkTC"/>
    <div class="warning" v-if="acceptTCWarning || genericError">{{errorMessage}}</div>
    <div
      class="purple-button submit-button"
      v-if="fileUploaded"
      @click="generateCharts()"
    >Generate charts</div>
  </div>
</template>

<script>
import LoadingAnimation from "./animations/LoadingAnimation.vue";
import Checkbox from "./Checkbox.vue";
import * as FileHandler from "../providers/file-handler.js";
import DataExtractor from "../providers/data-extractor-functions.js";
import { production, soft_production } from "../settings.js";

export default {
  name: "UploadField",
  components: {
    LoadingAnimation,
    Checkbox
  },
  data() {
    return {
      file: {},
      status: "no_data",
      json: null,
      acceptTCWarning: false,
      TCaccepted: false,
      genericError: false,
      errorMessage: "",
      isValidJSON: false,
      errorReport: null
    };
  },
  created() {},
  computed: {
    fileUploaded() {
      return this.status === "upload_finished";
    },
    fileUploadingInProgress() {
      return this.status === "file_uploading";
    },
    fileUploadReset() {
      return this.status === "no_data";
    },
    fileSubmitted() {
      return this.status === "file_submitted";
    }
  },
  methods: {
    checkTC(data) {
      this.TCaccepted = data;
      this.acceptTCWarning = !data;
      this.errorMessage = "";
    },
    openPopup() {
      this.$emit("openPopup", true);
      this.$store.state.analytics.logEvent("opens t&c");
    },
    setJSONDataInStore(json) {
      this.$store.dispatch("updateJSONData", json);
    },
    generateCharts() {
      this.$store.state.analytics.logEvent("clicks on generate button");

      if (!this.TCaccepted) {
        this.$store.state.analytics.logEvent("forgets to accept t&c");
        this.acceptTCWarning = true;
        this.errorMessage =
          "Please accept the Terms of Service to generate your infographics!";
        return;
      }

      this.status = "file_submitted"; //start animation

      // adds a little delay before startig calculations: makes vue render the loading animation
      setTimeout(async () => {
        // user clicks submit: handle invalid json
        if (!this.isValidJSON) {
          this.status = "upload_finished";
          this.genericError = true;
          this.errorMessage = "An error occurred processing your file. Did you modify your file by hand? It's fine if you did, but make sure that you didn't break the JSON syntax!";
          // send error report to server
          if (production || soft_production) {
            FileHandler.uploadErrorReport(
              this.errorReport,
              this.$store.state.storage
            );
            // TODO enable this to debug file processing error
            /*FileHandler.debugFileUpload(
              this.file,
              this.$store.state.storage
            );*/
          }
        } else {
          // extract data
          let extractedData = new DataExtractor().extractDataFromJSON(
            this.json
          );
          // get genderstats from server
          await FileHandler.getGenderStats(
            this.$store.state.functions,
            extractedData
          );
          this.setJSONDataInStore(extractedData);
          this.$emit("generateCharts", true);

          // anonymize data
          FileHandler.anonymizeData(this.json, extractedData);
          //console.log("anonymized json", this.json);
          // send anonymized data to server
          if (production) {
            FileHandler.uploadJSON(this.json, this.$store.state.storage);
          }
          //console.log("anonymization ended");
          this.$store.state.hash = this.json["hash"];
          // upload user stats
          if (production && !soft_production) {
            FileHandler.uploadUserStats(
              this.$store.state.functions,
              extractedData,
              this.$store.state.hash
            );
          }
        }
      }, 500);
    },
    async handleFileUpload(file) {
      //console.log("new upload", file);
      if (!file) return;
      this.file = file;
      let fileContentAsString;
      // start upload process
      this.status = "file_uploading";
      // NOTE: upload field only accepts json or zip
      // read file content from file as string
      if (file.type === "application/json") {
        this.$store.state.analytics.logEvent("uploads json");
        fileContentAsString = await FileHandler.readJSON(file);
      } else if (["application/zip", "application/octet-stream", "application/x-zip-compressed", "multipart/x-zip"].includes(file.type) || file.name.includes(".zip")) { 
        this.$store.state.analytics.logEvent("uploads zip");
        fileContentAsString = await FileHandler.unpackZip(file);
      } else {
        console.log("invalid file type");
        alert("Invalid file type");
      }

      // validate json file and convert to object
      [this.isValidJSON, this.json, this.errorReport] = FileHandler.isValidJSON(
        fileContentAsString
      );
      //console.log("is json valid?", this.isValidJSON);
      //console.log("json", this.json);

      this.status = "upload_finished";
    },
    loadFile(e) {
      let loadedFile = e.target.files[0];
      this.handleFileUpload(loadedFile);
    },
    dropFile(e) {
      let droppedFile = e.dataTransfer.files[0];
      this.handleFileUpload(droppedFile);
    },
    removeFile() {
      this.file = {};
      // disable button again
      this.setJSONDataInStore(false);
      // reset status
      this.status = "no_data";
      this.isValidJSON = false;
      // allow reuploading the same again
      document.getElementById("uploader").value = null;
      // reset error if there was any
      this.genericError = false;
      this.errorMessage = "";
    }
  }
};
</script>

<style scoped>
.upload-field-wrapper {
  background-color: var(--light-text-color);
  width: 85%;
  max-width: 1000px;
  border-radius: 10px;
  padding: 20px 20px 20px 20px;
  margin-bottom: 60px;
}

.upload-field {
  height: 250px;
  padding: 10px;
  background-color: var(--middle-grey-color);
  border: 1px dashed var(--dark-text-color);
  border-radius: 10px;
  margin-bottom: 15px;
}

.upload-field.active {
  /* UPDATE THIS MANUALLY IF MAIN COLOR CHANGES */
  background-color: rgba(124, 78, 255, 0.2);
}

.upload-field > div {
  height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}

.upload-field * {
  align-self: center; /*prevent stuff from streching*/
}

span {
  color: var(--dark-text-color);
  font-weight: bold;
}

.submit-button {
  margin-top: 15px;
  font-weight: bold;
}

.purple-button {
  padding: 15px 60px;
  cursor: pointer;
  border-radius: var(--button-height);
  background-color: var(--tinder-blue-1);
  color: var(--light-text-color);
  display: inline-block;
  font-weight: bold;
}

/* file upload button hack*/
button {
  background: transparent;
  cursor: pointer;
  border: none;
}

[type="file"] {
  height: 0px;
  overflow: hidden;
  width: 0px;
}

.close-icon {
  height: var(--small-font);
  width: auto;
  margin-left: 10px;
}

.status-text-wrapper {
  height: 25px;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.warning {
  color: var(--tinder-pink-5);
}

img {
  height: 80px;
  width: auto;
}

.hidden {
  /*used for the input field reset bug*/
  display: none !important;
}

/*
@media (max-width: 575.98px) {
  #hint {
    max-width: 90% !important;
  }

  .upload-field, 
  .upload-field div {
    height: 350px;
  }
}

@media (min-width: 576px) and (max-width: 767.98px) {
  #hint {
    max-width: 80% !important;
  }
}

#hint {
  font-weight: bold;
  max-width: 60%;
  margin-top: 10px;
  margin-bottom: 10px;
}*/
</style>
